/**
 * Formats a currency value (USD)
 * @param value The number to format
 * @returns Formatted currency string
 */
export const formatCurrency = (value: number | undefined | null): string => {
  if (value === undefined || value === null) {
    return '$0';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Formats a phone number for display purposes only.
 * This doesn't affect how the data is stored in the backend.
 *
 * @param phoneNumber The raw phone number string
 * @returns Formatted phone number string
 */
/**
 * A display string, or undefined when the value is missing. Some API rows carry the
 * text "null" or "undefined" instead of a real null, so those count as missing too.
 */
export const presentText = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const text = value.trim();
  return text && !/^(null|undefined)$/i.test(text) ? text : undefined;
};

export const formatPhoneNumber = (phoneNumber: string | null | undefined): string => {
  if (!phoneNumber) return '-';

  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '');

  // Format based on length
  if (cleaned.length === 10) {
    // US format: (XXX) XXX-XXXX
    return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    // US with country code: +1 (XXX) XXX-XXXX
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7, 11)}`;
  } else {
    // For other formats, add spaces every 3 digits for readability
    // but preserve the original format if it's not a standard length
    return phoneNumber;
  }
};

/**
 * Picks which of a call's two numbers identifies the other party.
 *
 * Call tables used to render `to_number` for every row, so inbound rows showed
 * our own Remi number instead of the caller (SPEC-2382). Direction decides
 * whose number is the counterparty; the fallback keeps a number on screen when
 * the preferred side is missing rather than rendering a bare dash.
 *
 * @param call Call row with a normalized `inbound` flag and both numbers
 * @returns The counterparty number, or null when neither side has one
 */
export const selectCallDisplayNumber = (call: {
  inbound?: boolean | null;
  from_number?: string | null;
  to_number?: string | null;
}): string | null => {
  const preferred = call.inbound ? call.from_number : call.to_number;
  const fallback = call.inbound ? call.to_number : call.from_number;

  return preferred || fallback || null;
};
